import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPostJson } from '../api.js';

// "Generate all videos" as page-level state. The batch runs on the server
// (src/web/cutVideoBatch.js) for hours; the Scenes page polls its snapshot
// here so every cut can show whether it is waiting, rendering, done or
// failed, and a page opened the next morning picks the batch up again.
// ScenesPanel owns the store (useCutVideoBatchStore) and provides it.

const POLL_MS = 4000;

export function isBatchRunning(batch) {
  return batch?.status === 'running';
}

export function useCutVideoBatchStore(beatId, onRefresh) {
  const [batch, setBatch] = useState(null);
  const [dismissedId, setDismissedId] = useState(null);
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  const settledRef = useRef(null); // how many cuts had ended at the last poll

  const apply = useCallback((next) => {
    setBatch(next || null);
    // A cut that just ended has a new clip (or an error) to show.
    const settled = next ? `${next.batch_id}:${next.counts.done + next.counts.error}` : null;
    if (settledRef.current !== null && settled !== settledRef.current) refreshRef.current?.();
    settledRef.current = settled;
  }, []);

  const load = useCallback(async () => {
    if (!beatId) return;
    try {
      const r = await apiGet(`/cuts/videos/batch?beat_id=${encodeURIComponent(beatId)}`);
      apply(r?.batch);
    } catch {
      // transient — the next poll tries again
    }
  }, [beatId, apply]);

  useEffect(() => {
    setBatch(null);
    settledRef.current = null;
    load();
  }, [beatId, load]);

  const running = isBatchRunning(batch);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const cancel = useCallback(async () => {
    const r = await apiPostJson('/cuts/videos/batch/cancel', { beat_id: beatId });
    apply(r?.batch);
  }, [beatId, apply]);

  const dismiss = useCallback(() => setDismissedId(batch?.batch_id || null), [batch]);

  const items = useMemo(() => {
    const map = {};
    for (const it of batch?.items || []) map[it.cut_id] = it;
    return map;
  }, [batch]);

  const visible = batch && batch.batch_id !== dismissedId ? batch : null;
  return useMemo(
    () => ({ batch: visible, items: visible ? items : {}, running, started: apply, cancel, dismiss }),
    [visible, items, running, apply, cancel, dismiss],
  );
}

const CutVideoBatchContext = createContext(null);

export function CutVideoBatchProvider({ store, children }) {
  return <CutVideoBatchContext.Provider value={store}>{children}</CutVideoBatchContext.Provider>;
}

export function useCutVideoBatch() {
  return useContext(CutVideoBatchContext);
}

// The chip a cut shows while a batch has it: spinning while it renders.
export function CutBatchStatus({ item }) {
  if (!item) return null;
  if (item.status === 'running') {
    return (
      <span className="cut-batch-status is-running" title="This cut is being generated by the batch">
        <span className="spinner cut-batch-spinner" aria-hidden="true" />
        Generating video…
      </span>
    );
  }
  if (item.status === 'queued') {
    return <span className="cut-batch-status is-queued" title="Waiting for its turn in the batch">Queued in batch</span>;
  }
  if (item.status === 'error') {
    return <span className="cut-batch-status is-error" title={item.error || ''}>Batch failed: {item.error || 'render failed'}</span>;
  }
  if (item.status === 'done') return <span className="cut-batch-status is-done">Generated by batch</span>;
  return null;
}

// The page banner: progress while it runs, the tally once it has ended.
export function CutBatchBanner({ store }) {
  const [busy, setBusy] = useState(false);
  const batch = store?.batch;
  if (!batch) return null;
  const c = batch.counts;
  const todo = c.total - c.skipped;
  const provider = batch.provider === 'comfy' ? 'ComfyUI' : 'fal.ai';
  if (isBatchRunning(batch)) {
    return (
      <div className="cut-job-panel is-running cut-batch-banner">
        <span className="spinner cut-batch-spinner" aria-hidden="true" />
        <span>
          Generating all videos ({provider} · {batch.model_id}): {c.done + c.error} of {todo} finished
          {c.running ? ` · ${c.running} rendering` : ''}
          {c.queued ? ` · ${c.queued} waiting` : ''}
          {c.error ? ` · ${c.error} failed` : ''}. It keeps going with this page closed.
        </span>
        <button
          type="button"
          className="danger"
          disabled={busy}
          onClick={async () => {
            if (!confirm('Stop the batch? The cut rendering now finishes; the cuts still waiting are not started.')) return;
            setBusy(true);
            try { await store.cancel(); } catch {} finally { setBusy(false); }
          }}
        >
          Stop batch
        </button>
      </div>
    );
  }
  const cls = c.error ? (c.done ? 'is-partial' : 'is-error') : 'is-done';
  return (
    <div className={`cut-job-panel ${cls} cut-batch-banner`}>
      <span>
        Batch {batch.status === 'cancelled' ? 'stopped' : 'finished'} ({provider} · {batch.model_id}): {c.done} generated
        {c.error ? ` · ${c.error} failed` : ''}
        {c.cancelled ? ` · ${c.cancelled} not started` : ''}
        {c.skipped ? ` · ${c.skipped} skipped` : ''}.
      </span>
      <button type="button" onClick={store.dismiss}>Dismiss</button>
    </div>
  );
}
